import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Field, Input, InputProps } from '~components/Form'
import { IconName } from '~components/Icon'
import { Dropdown } from '~components/Dropdown'
import DropDownContent from './DropDownContent'
import * as Styled from './TimePicker.styles'
import { TimeTuple, AMPM } from './TimePicker.types'
import { formatFullTime } from './TimePicker.utils'

export interface Props {
  error?: string
  placeholder?: string
  time: Date
  onChange: (time: Date) => void
  withIcon?: boolean
  size?: InputProps['size']
}

const TimePicker: React.FC<Props> = ({ placeholder, error, time, onChange, size, withIcon = true }) => {
  const timeTuple: TimeTuple = useMemo(() => {
    let hour = time.getHours()
    const minute = time.getMinutes()
    let ampm: AMPM = 'AM'

    if (hour >= 12) {
      hour -= 12
      ampm = 'PM'
    }

    if (hour === 0) {
      hour = 12
    }

    return [hour, minute, ampm]
  }, [time])

  const [timeStr, setTimeStr] = useState<string>('')

  useEffect(() => {
    setTimeStr(formatFullTime(timeTuple))
  }, [timeTuple])

  const onTimeSelected = useCallback(
    (t: TimeTuple) => {
      const [h, minutes, ampm] = t
      let hours = h

      if (ampm === 'PM') {
        if (hours !== 12) {
          hours = (hours + 12) % 24
        }
      }

      if (ampm === 'AM' && hours === 12) {
        hours = 0
      }

      const newTime = new Date(time)

      newTime.setHours(hours)
      newTime.setMinutes(minutes)
      onChange(newTime)
    },
    [time, onChange, time],
  )

  const errorText = useMemo(() => error || 'Invalid time', [error])
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    setHasError(false)
  }, [timeTuple])

  const onTimeInputChange = useCallback(
    (val: string) => {
      setTimeStr(val)

      const match = val.match(/^(\d{2}):(\d{2}) (AM|PM)$/i)

      if (!match) {
        setHasError(true)
        return
      }

      const [_full, hoursStr, minutesStr, ampm] = match

      const hours = parseInt(hoursStr, 10)
      const minutes = parseInt(minutesStr, 10)

      if (hours > 12 || minutes > 59) {
        setHasError(true)
        return
      }

      if (!['AM', 'PM'].includes(ampm.toUpperCase())) {
        setHasError(true)
        return
      }

      // Without this we'll see stale error when parent wraps this in useMemo even if deps include time
      setHasError(false)

      onTimeSelected([hours, minutes, ampm.toUpperCase() as AMPM])
    },
    [onTimeSelected],
  )

  const [isDropdownOpen, setDropdownOpen] = useState(false)

  return (
    <Dropdown shouldDismissOnClick={true} isOpen={isDropdownOpen} onOpenChange={setDropdownOpen}>
      <Field error={hasError && errorText}>
        <Input
          isError={hasError}
          icon={withIcon ? IconName.clock : undefined}
          type="text"
          size={size}
          placeholder={placeholder || 'Time'}
          value={timeStr}
          onValueChange={val => onTimeInputChange(val)}
        />
      </Field>
      <Styled.DropDownContainer>
        <DropDownContent time={timeTuple} onChange={t => onTimeSelected(t)} />
      </Styled.DropDownContainer>
    </Dropdown>
  )
}

export default TimePicker
